import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, MessageCircle, LifeBuoy, Send, ArrowRight, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { MarketingHero, SectionHead, fadeUp } from "../components/marketing/Blocks";
import api from "../lib/api";

const CONTACT_EMAIL = "shipanylogistics@gmail.com";

const inputCls = "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-shipany-cyan focus:outline-none";

export default function Contact() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });

  useEffect(() => {
    api.get("/public/site-settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const wa = (settings?.whatsapp_number || "").replace(/\D/g, "");

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message");
      return;
    }
    const subject = encodeURIComponent(`[ShipAny] ${form.subject.trim() || "Support request"} — ${form.name.trim()}`);
    const body = encodeURIComponent(`${form.message.trim()}\n\n— ${form.name.trim()} (${form.email.trim()})`);
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    toast.success("Opening your email app with the message pre-filled…");
  };

  const cards = [
    { icon: Mail, title: "Email Us", desc: "For support, sales and partnerships.", action: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}`, testId: "contact-email-card" },
    ...(wa ? [{ icon: MessageCircle, title: "WhatsApp", desc: "Chat with the ShipAny team.", action: settings.whatsapp_number, href: `https://wa.me/${wa}`, testId: "contact-whatsapp-card" }] : []),
    ...(settings?.support_phone ? [{ icon: Phone, title: "Call Us", desc: "Speak to the ShipAny team.", action: settings.support_phone, href: `tel:${settings.support_phone.replace(/\s/g, "")}`, testId: "contact-phone-card" }] : []),
    ...(settings?.address ? [{ icon: MapPin, title: "Office", desc: settings.address, action: null, testId: "contact-address-card", plain: true }] : []),
    { icon: LifeBuoy, title: "In-App Support", desc: "Already a customer? Raise a ticket from your dashboard for the fastest resolution.", action: "Open Support", href: "/app/support", testId: "contact-support-card", internal: true },
  ];

  return (
    <MarketingLayout>
      <div data-testid="contact-page">
        <MarketingHero kicker="Contact" title="Talk to the ShipAny Team"
          sub="Questions about shipping, pricing, integrations or your account — reach us on email or WhatsApp, or send a message using the form below." />

        <section className="bg-[#071B3A] py-16 sm:py-20" data-testid="contact-cards-section">
          <div className="mx-auto max-w-6xl px-5">
            <div className={`grid gap-5 sm:grid-cols-2 ${cards.length > 2 ? "lg:grid-cols-3" : ""}`}>
              {cards.map((c, i) => {
                const inner = (
                  <>
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-shipany-blue/40 to-shipany-cyan/20 transition-transform duration-300 group-hover:scale-110">
                      <c.icon className="h-5 w-5 text-shipany-cyan" />
                    </span>
                    <h3 className="font-display mt-4 text-lg font-bold text-white">{c.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{c.desc}</p>
                    {c.action && (
                      <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#FF9933]">
                        {c.action} <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    )}
                  </>
                );
                const cls = "group rounded-2xl border border-white/10 bg-white/[0.04] p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-shipany-cyan/40";
                return (
                  <motion.div key={c.testId} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }}>
                    {c.plain ? <div data-testid={c.testId} className={cls}>{inner}</div>
                      : c.internal
                        ? <Link to={c.href} data-testid={c.testId} className={`block ${cls}`}>{inner}</Link>
                        : <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={c.testId} className={`block ${cls}`}>{inner}</a>}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-shipany-navy py-16 sm:py-20" data-testid="contact-form-section">
          <div className="mx-auto max-w-2xl px-5">
            <SectionHead kicker="Message us" title="Send a Message" sub="Fill in the form — it opens your email app with everything pre-filled, addressed to our team." />
            <motion.form {...fadeUp} onSubmit={submit} data-testid="contact-form"
              className="mt-10 space-y-4 rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-xs font-semibold text-slate-300">Your name</label>
                  <input id="contact-name" data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Full name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-xs font-semibold text-slate-300">Email address</label>
                  <input id="contact-email" type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@company.com" className={inputCls} />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="mb-1.5 block text-xs font-semibold text-slate-300">Subject (optional)</label>
                <input id="contact-subject" data-testid="contact-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="e.g. Pricing for bulk shipping" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-xs font-semibold text-slate-300">Message</label>
                <textarea id="contact-message" data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="How can we help?" rows={5} className={inputCls} />
              </div>
              <button data-testid="contact-submit-btn" type="submit"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.4)]">
                <Send className="h-4 w-4" /> Send via Email
              </button>
              <p className="text-center text-xs text-slate-500" data-testid="contact-form-note">
                Opens your email app addressed to <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-direct-email-link" className="text-shipany-cyan hover:underline">{CONTACT_EMAIL}</a>
              </p>
            </motion.form>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}
